import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

import { api } from './api'
import { miscItemsApi } from './miscItems'
import { quoteItemsApi } from './quoteItems'

const apiVersion = 'v2'
const calculateQuoteChains = new Map()

/**
 * How `adjustQuotePrice` reads `value`. Mirrors the API's QuoteAdjustmentType enum,
 * which is sent as a number. DesiredFinalResult (2) is left out until the API supports it.
 */
export const QUOTE_ADJUSTMENT_TYPE = {
    Percentage: 0,
    MonetaryValue: 1,
}

export const quotesApi = api
    .enhanceEndpoints({
        addTagTypes: ['Quotes', 'CustomerQuotes'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getQuotes: builder.query({
                query: ({ organisationId, params }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes`,
                    params: {
                        organisationId: organisationId,
                        pageNumber: params.pageNumber,
                        pageSize: params.pageSize,
                        searchTerm: params.searchTerm || '',
                        orderedByProperty: params.orderedByProperty,
                        orderedBy: params.orderedBy,
                        includeArchived: params.includeArchived,
                        status: params.status,
                        lastModifiedBy: params.lastModifiedBy,
                        lastModifiedFrom: params.lastModifiedFrom,
                        lastModifiedTo: params.lastModifiedTo,
                        source: params.source,
                    },
                }),
                providesTags: (result, _error, _arg) =>
                    result
                        ? [{ type: 'Quotes', id: 'LIST' }, ...result.quotes.map(({ id }) => ({ type: 'Quotes', id }))]
                        : [{ type: 'Quotes', id: 'LIST' }],
            }),

            getQuotesByCustomer: builder.query({
                query: ({ organisationId, params }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes`,
                    params: {
                        organisationId: organisationId,
                        customerId: params.customerId,
                        pageNumber: params.pageNumber,
                        pageSize: params.pageSize,
                        searchTerm: params.searchTerm || '',
                        orderedByProperty: params.orderedByProperty,
                        orderedBy: params.orderedBy,
                        includeArchived: params.includeArchived,
                        status: params.status,
                    },
                }),
                providesTags: (result, _error, _arg) =>
                    result
                        ? [
                              { type: 'CustomerQuotes', id: 'LIST' },
                              ...result.quotes.map(({ id }) => ({ type: 'CustomerQuotes', id })),
                          ]
                        : [{ type: 'CustomerQuotes', id: 'LIST' }],
            }),

            createQuote: builder.mutation({
                query: ({ name, organisationId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes`,
                    method: 'POST',
                    body: {
                        name,
                    },
                }),
                invalidatesTags: [{ type: 'Quotes', id: 'LIST' }, 'Organisation'],
            }),

            getQuote: builder.query({
                query: ({ organisationId, params, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}`,
                    params,
                }),
                providesTags: (_result, _error, arg) => [{ type: 'Quotes', id: arg.quoteId }],
            }),

            getSharedQuote: builder.query({
                query: ({ params, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${null}/quotes/${quoteId}/shared`,
                    params,
                }),
                providesTags: (_result, _error, arg) => [{ type: 'Quotes', id: arg.quoteId }],
            }),

            updateQuote: builder.mutation({
                query: ({ organisationId, quote, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}`,
                    method: 'PUT',
                    body: quote,
                }),
                async onQueryStarted({ organisationId, quote, quoteId }, { dispatch, queryFulfilled }) {
                    dispatch(
                        quotesApi.util.updateQueryData('getQuote', { organisationId, quoteId }, (draft) => {
                            Object.assign(draft, quote)
                        })
                    )
                    try {
                        const { data } = await queryFulfilled
                        // Only server-computed fields: the PUT response lacks GET-only fields such as quoteItemIds
                        dispatch(
                            quotesApi.util.updateQueryData('getQuote', { organisationId, quoteId }, (draft) => {
                                // Clearing the date sends CLEAR_DATE; the server replies with the automatic date
                                if (data?.expiryDate) draft.expiryDate = data.expiryDate
                                draft.isExpiryDateSetManually = data?.isExpiryDateSetManually
                                draft.earliestSheetExpiryDate = data?.earliestSheetExpiryDate
                                draft.hasSheetExpiringBeforeExpiryDate = data?.hasSheetExpiringBeforeExpiryDate
                            })
                        )
                        // Invalidate CustomerQuotes to refresh quote history when customer changes
                        dispatch(quotesApi.util.invalidateTags([{ type: 'CustomerQuotes', id: 'LIST' }]))
                        // Invalidate Quotes LIST to refresh main quotes dashboard
                        //dispatch(quotesApi.util.invalidateTags([{ type: 'Quotes', id: 'LIST' }]))
                    } catch {
                        dispatch(quotesApi.util.invalidateTags([{ type: 'Quotes', id: quoteId }]))
                        dispatch(quotesApi.util.invalidateTags([{ type: 'CustomerQuotes', id: 'LIST' }]))
                    }
                },
            }),

            enableQuote: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/enable`,
                    method: 'POST',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'QuoteItems', id: 'LIST' },
                ],
            }),

            orderQuote: builder.mutation({
                query: ({ organisationId, params, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/order`,
                    method: 'POST',
                    params,
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'QuoteItems', id: 'LIST' },
                ],
            }),

            invoiceQuote: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/invoice`,
                    method: 'POST',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'QuoteItems', id: 'LIST' },
                ],
            }),

            paragonInvoiceQuote: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/paragon-invoice`,
                    method: 'POST',
                    timeout: 60000,
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'QuoteItems', id: 'LIST' },
                ],
            }),

            unarchiveQuote: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/unarchive`,
                    method: 'PUT',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: 'LIST' },
                    { type: 'Quotes', id: arg.quoteId },
                ],
            }),

            deleteQuote: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: 'LIST' },
                    { type: 'Quotes', id: arg.quoteId },
                ],
            }),

            duplicateQuote: builder.mutation({
                query: ({ newName, organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/duplicate`,
                    method: 'POST',
                    body: {
                        newName,
                    },
                }),
                invalidatesTags: (_result, _error, _arg) => [{ type: 'Quotes', id: 'LIST' }],
            }),

            calculateQuote: builder.mutation({
                queryFn: async ({ organisationId, quoteId }, _api, _extraOptions, baseQuery) => {
                    const runRequest = () =>
                        baseQuery({
                            url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/calculate`,
                            method: 'POST',
                            // Large quotes take longer than the global 30 s; 100 s is Cloudflare's cap in production.
                            timeout: 100000,
                        })

                    const previous = calculateQuoteChains.get(quoteId) ?? Promise.resolve()
                    const run = previous.then(runRequest, runRequest)
                    calculateQuoteChains.set(quoteId, run)

                    try {
                        return await run
                    } finally {
                        if (calculateQuoteChains.get(quoteId) === run) {
                            calculateQuoteChains.delete(quoteId)
                        }
                    }
                },
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'QuoteItems', id: 'LIST' },
                    { type: 'MiscItems', id: 'LIST' },
                    { type: 'PartLibraryEntries', id: 'LIST' },
                    { type: 'PriceDetails', id: 'LIST' },
                ],
            }),

            adjustQuotePrice: builder.mutation({
                query: ({ organisationId, quoteId, type, value }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/adjust-price`,
                    method: 'PUT',
                    body: { value, type },
                    // The API answers a rejected adjustment with a text/plain 400. Without this the
                    // default JSON handler turns it into a PARSING_ERROR and hides the status.
                    responseHandler: 'content-type',
                }),
                async onQueryStarted({ organisationId, quoteId }, { dispatch, queryFulfilled }) {
                    let data
                    try {
                        ;({ data } = await queryFulfilled)
                    } catch {
                        // The API rejected the change, so nothing was saved and the caches are still right
                        return
                    }

                    const args = { organisationId, quoteId }
                    // The response holds bare items without attachments, secondary processes or fresh
                    // prices, so copy only the adjustment fields onto the cached items
                    dispatch(
                        quoteItemsApi.util.updateQueryData('getQuoteItems', args, (draft) => {
                            data.quoteItems.forEach(
                                ({ id, isOtherChargePercentage, otherChargePercentage, otherChargePrice }) => {
                                    const item = draft.find((cached) => cached.id === id)
                                    if (item) {
                                        Object.assign(item, {
                                            isOtherChargePercentage,
                                            otherChargePercentage,
                                            otherChargePrice,
                                        })
                                    }
                                }
                            )
                        })
                    )
                    dispatch(
                        miscItemsApi.util.updateQueryData('getMiscItems', args, (draft) => {
                            data.miscellaneousItems.forEach(
                                ({ id, isPriceAdjustmentPercentage, priceAdjustment, priceAdjustmentPercentage }) => {
                                    const item = draft.find((cached) => cached.id === id)
                                    if (item) {
                                        Object.assign(item, {
                                            isPriceAdjustmentPercentage,
                                            priceAdjustmentPercentage,
                                            priceAdjustment,
                                        })
                                    }
                                }
                            )
                        })
                    )
                    // The API sets the quote to NotCalculated; mirror it without refetching the quote
                    dispatch(
                        quotesApi.util.updateQueryData('getQuote', args, (draft) => {
                            draft.status = QUOTE_STATUS_ARRAY.NotCalculated
                        })
                    )
                },
            }),

            calculateDeliveryPrice: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/calculate-delivery-price`,
                    method: 'POST',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'PriceDetails', id: 'LIST' },
                ],
            }),

            calculateTaxRate: builder.mutation({
                query: ({ organisationId, quoteId }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/calculate-tax-rate`,
                    method: 'POST',
                }),
                invalidatesTags: (_result, _error, arg) => [
                    { type: 'Quotes', id: arg.quoteId },
                    { type: 'PriceDetails', id: 'LIST' },
                ],
            }),

            getQuoteAvailableDocuments: builder.query({
                query: ({ organisationId, quoteId }) =>
                    `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/available-documents`,
            }),

            sendInternalQuoteEmail: builder.mutation({
                query: ({
                    bccEmailAddresses,
                    ccEmailAddresses,
                    documentTypes,
                    includePdf,
                    message,
                    organisationId,
                    quoteId,
                    subject,
                    toEmailAddress,
                }) => ({
                    url: `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/send`,
                    method: 'POST',
                    body: {
                        subject,
                        message,
                        toEmailAddress,
                        ccEmailAddresses,
                        bccEmailAddresses,
                        includePdf,
                        documentTypes,
                    },
                    // The API renders every selected document before it queues the email.
                    // 100 s is the Cloudflare cap in front of the production API.
                    timeout: 100000,
                    // The API answers a bad address with a text/plain 400. Without this the
                    // default JSON handler turns it into a PARSING_ERROR and hides the status.
                    responseHandler: 'content-type',
                }),
            }),
        }),
    })

export const selectQuotes = (state, query) => {
    return quotesApi.endpoints.getQuotes.select(query)(state)?.data ?? []
}

export const selectQuote = (state, query) => {
    return quotesApi.endpoints.getQuote.select(query)(state)?.data ?? {}
}

export const {
    useAdjustQuotePriceMutation,
    useCalculateDeliveryPriceMutation,
    useCalculateQuoteMutation,
    useCalculateTaxRateMutation,
    useCreateQuoteMutation,
    useDeleteQuoteMutation,
    useDuplicateQuoteMutation,
    useEnableQuoteMutation,
    useGetQuoteAvailableDocumentsQuery,
    useGetQuoteQuery,
    useGetQuotesByCustomerQuery,
    useGetQuotesQuery,
    useGetSharedQuoteQuery,
    useInvoiceQuoteMutation,
    useLazyGetQuoteQuery,
    useLazyGetQuotesByCustomerQuery,
    useLazyGetQuotesQuery,
    useLazyGetSharedQuoteQuery,
    useOrderQuoteMutation,
    useParagonInvoiceQuoteMutation,
    useSendInternalQuoteEmailMutation,
    useUnarchiveQuoteMutation,
    useUpdateQuoteMutation,
} = quotesApi
